class Neuron {
    constructor(x, y, color) {
        this.x = x;
        this.y = y;
        this.targetX = x;
        this.targetY = y;
        this.vx = 0;
        this.vy = 0;
        this.color = color;
        this.connections = new Set();
        this.radius = Math.random() * 2 + 2;
        this.speed = 0.02;
        this.maxConnections = Math.floor(Math.random() * 4) + 4;
        this.wanderAngle = Math.random() * Math.PI * 2;
    }

    update(width, height) {
        // Wander behavior
        this.wanderAngle += (Math.random() - 0.5) * 0.5;
        this.vx += Math.cos(this.wanderAngle) * this.speed;
        this.vy += Math.sin(this.wanderAngle) * this.speed;

        // Apply friction
        this.vx *= 0.99;
        this.vy *= 0.99;

        // Update position
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around edges
        if (this.x < -50) this.x = width + 50;
        if (this.x > width + 50) this.x = -50;
        if (this.y < -50) this.y = height + 50;
        if (this.y > height + 50) this.y = -50;
    }
}

export class NeuralBackground {
    constructor() {
        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'fixed';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.style.zIndex = '-1';
        this.canvas.style.opacity = '0';
        this.canvas.style.transition = 'opacity 0.3s ease';
        this.canvas.style.pointerEvents = 'none';
        this.canvas.style.background = 'black';

        this.ctx = this.canvas.getContext('2d');
        this.isActive = false;
        this.animationFrame = null;

        // Colors for each hemisphere
        this.leftColor = { r: 0, g: 128, b: 64 };   // Green
        this.rightColor = { r: 255, g: 200, b: 0 }; // Yellow
        
        // Neural network settings
        this.neurons = [];
        this.maxNeurons = 80;
        this.connectionDistance = 200;
        this.connectionOpacityDivisor = 2;
        this.fadeSpeed = 0.1;
        
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);
    }

    init() {
        document.body.appendChild(this.canvas);
        this.handleResize();
        this.createNeurons();
    }

    handleResize() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.canvas.width = this.width;
        this.canvas.height = this.height;
        this.centerX = this.width / 2;
        this.centerY = this.height / 2;
    }

    createNeurons() {
        this.neurons = [];
        for (let i = 0; i < this.maxNeurons; i++) {
            const x = Math.random() * this.width;
            const y = Math.random() * this.height;
            
            // Determine color based on position relative to center
            const color = this.getNeuronColor(x);
            
            this.neurons.push(new Neuron(x, y, color));
        }
    }

    getNeuronColor(x) {
        // Calculate blend based on position
        const blend = Math.min(1, Math.max(0, (x - this.centerX + 200) / 400));
        
        return {
            r: this.leftColor.r * (1 - blend) + this.rightColor.r * blend,
            g: this.leftColor.g * (1 - blend) + this.rightColor.g * blend,
            b: this.leftColor.b * (1 - blend) + this.rightColor.b * blend
        };
    }

    show() {
        if (!this.isActive) {
            this.isActive = true;
            this.canvas.style.opacity = '1';
            this.createNeurons();
            this.animate();
        }
    }

    hide() {
        if (this.isActive) {
            this.isActive = false;
            this.canvas.style.opacity = '0';
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }
        }
    }

    animate() {
        if (!this.isActive) return;

        // Clear canvas with slight fade
        this.ctx.fillStyle = `rgba(0,0,0,${this.fadeSpeed})`;
        this.ctx.fillRect(0, 0, this.width, this.height);

        // Update and draw neurons
        this.neurons.forEach(neuron => {
            neuron.update(this.width, this.height);
            neuron.connections.clear();
        });

        // Draw connections
        this.ctx.beginPath();
        for (let i = 0; i < this.neurons.length; i++) {
            const neuron1 = this.neurons[i];
            
            for (let j = i + 1; j < this.neurons.length; j++) {
                const neuron2 = this.neurons[j];
                
                // Calculate distance
                const dx = neuron2.x - neuron1.x;
                const dy = neuron2.y - neuron1.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance < this.connectionDistance && 
                    neuron1.connections.size < neuron1.maxConnections &&
                    neuron2.connections.size < neuron2.maxConnections) {
                    
                    // Add connection
                    neuron1.connections.add(neuron2);
                    neuron2.connections.add(neuron1);
                    
                    // Draw connection with blended color and distance-based opacity
                    const opacity = Math.pow(1 - distance / this.connectionDistance, 2) / this.connectionOpacityDivisor;
                    const color = {
                        r: (neuron1.color.r + neuron2.color.r) / 2,
                        g: (neuron1.color.g + neuron2.color.g) / 2,
                        b: (neuron1.color.b + neuron2.color.b) / 2
                    };
                    
                    this.ctx.strokeStyle = `rgba(${color.r},${color.g},${color.b},${opacity})`;
                    this.ctx.lineWidth = (1 - distance / this.connectionDistance) * 2;
                    this.ctx.beginPath();
                    this.ctx.moveTo(neuron1.x, neuron1.y);
                    this.ctx.lineTo(neuron2.x, neuron2.y);
                    this.ctx.stroke();
                }
            }
        }

        // Draw neurons
        this.neurons.forEach(neuron => {
            this.ctx.fillStyle = `rgba(${neuron.color.r},${neuron.color.g},${neuron.color.b},0.6)`;
            this.ctx.beginPath();
            this.ctx.arc(neuron.x, neuron.y, neuron.radius, 0, Math.PI * 2);
            this.ctx.fill();
        });

        this.animationFrame = requestAnimationFrame(() => this.animate());
    }

    cleanup() {
        window.removeEventListener('resize', this.handleResize);
        if (this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
        }
    }
}
